import { type MutableRefObject, useCallback, useEffect, useRef } from 'react'
import { useMutation } from '@apollo/client/react'
import * as R from 'ramda'

import { CalculateDifferenceDistributionDocument } from '~/api/graphql/generated/types'
import { hasInvoiceLineItemId } from '~/components/dashboard/invoices/invoiceLineItemIdentity'
import type { InvoiceLineItem, InvoiceOrEstimate } from '~/types'
import {
  getExtendedPrice,
  getHighValue,
  getLowValue,
  getTotal,
  isRange,
  setHighValue,
  setLowValue,
} from '~/utils/invoiceUtils'

export type RunFixedBundleDifferenceDistributionParams = {
  bundleFixedPriceForDistribution: number
  invoiceGroupIndex: number
  itemIndex: number
  itemParent: InvoiceLineItem
  updatedParent: InvoiceLineItem
}

export function useFixedBundleDifferenceDistribution<
  T extends InvoiceOrEstimate,
>({
  invoiceRef,
  isSetBundlePriceEnabled,
  updateInvoice,
}: {
  invoiceRef: MutableRefObject<T>
  isSetBundlePriceEnabled: boolean
  updateInvoice: (invoice: T) => void
}) {
  const distributionAbortRef = useRef<AbortController>()
  const distributionSequenceRef = useRef(0)
  const [calculateDifferenceDistribution] = useMutation(
    CalculateDifferenceDistributionDocument,
  )

  useEffect(
    () => () => {
      distributionAbortRef.current?.abort()
    },
    [],
  )

  const calculateDistribution = useCallback(
    async ({
      values,
      fixedSum,
      signal,
    }: {
      fixedSum: number
      signal: AbortSignal
      values: number[]
    }) => {
      const result = await calculateDifferenceDistribution({
        variables: {
          values,
          fixedSum,
        },
        context: {
          fetchOptions: {
            signal,
          },
        },
      })

      return result.data?.calculateDifferenceDistribution || []
    },
    [calculateDifferenceDistribution],
  )

  const runFixedBundleDifferenceDistribution = useCallback(
    async ({
      bundleFixedPriceForDistribution,
      invoiceGroupIndex,
      itemIndex,
      itemParent,
      updatedParent,
    }: RunFixedBundleDifferenceDistributionParams) => {
      distributionAbortRef.current?.abort()
      const abortController = new AbortController()
      distributionAbortRef.current = abortController

      distributionSequenceRef.current += 1
      const requestSequence = distributionSequenceRef.current

      const extendedPrices = updatedParent.items!.map((child) =>
        getExtendedPrice(child),
      )
      const hasRangeValues = extendedPrices.some((price) => isRange(price))

      try {
        const [discountAmountsLow, discountAmountsHigh] = hasRangeValues
          ? await Promise.all([
              calculateDistribution({
                values: extendedPrices.map((price) => getLowValue(price)),
                fixedSum: bundleFixedPriceForDistribution,
                signal: abortController.signal,
              }),
              calculateDistribution({
                values: extendedPrices.map((price) => getHighValue(price)),
                fixedSum: bundleFixedPriceForDistribution,
                signal: abortController.signal,
              }),
            ])
          : [
              await calculateDistribution({
                values: extendedPrices.map((price) => getHighValue(price)),
                fixedSum: bundleFixedPriceForDistribution,
                signal: abortController.signal,
              }),
              null,
            ]

        if (
          requestSequence !== distributionSequenceRef.current ||
          abortController.signal.aborted
        ) {
          return
        }

        const baseInvoice = invoiceRef.current
        const distributedItems = updatedParent.items!.map((child, index) => {
          const distributedAmount =
            discountAmountsHigh != null && discountAmountsLow != null
              ? setHighValue(
                  setLowValue(
                    discountAmountsHigh[index],
                    discountAmountsLow[index],
                  ),
                  discountAmountsHigh[index],
                )
              : discountAmountsLow[index]
          const withDiscount = {
            ...child,
            ...('discount' in child ? { discount: null } : {}),
            discountAmount: distributedAmount,
            discountPerc: null,
          }
          return {
            ...withDiscount,
            subTotal: getTotal(
              withDiscount,
              baseInvoice,
              isSetBundlePriceEnabled,
            ) as number,
          }
        })
        const finalParent: InvoiceLineItem = {
          ...updatedParent,
          items: distributedItems,
          subTotal: getTotal(
            { ...updatedParent, items: distributedItems },
            baseInvoice,
            isSetBundlePriceEnabled,
          ) as number,
        }
        const prevInvoice = invoiceRef.current
        const prevGroups = prevInvoice.groups || []
        const prevGroup = prevGroups[invoiceGroupIndex]
        if (!prevGroup) {
          return
        }
        const newGroupedItems = prevGroup.groupedItems.map(
          (gi: InvoiceLineItem, idx: number) => {
            if (hasInvoiceLineItemId(itemParent)) {
              return gi.id === itemParent.id ? finalParent : gi
            }
            return idx === itemIndex ? finalParent : gi
          },
        )
        updateInvoice({
          ...prevInvoice,
          groups: R.update(
            invoiceGroupIndex,
            { ...prevGroup, groupedItems: newGroupedItems },
            prevGroups,
          ),
        })
      } catch (error) {
        if (!abortController.signal.aborted) {
          throw error
        }
      }
    },
    [calculateDistribution, invoiceRef, isSetBundlePriceEnabled, updateInvoice],
  )

  return { runFixedBundleDifferenceDistribution }
}
